import { every, isEmpty } from "../../utils/lodash";
import { QuestionType } from "../enums/QuestionType";
import { NavigationResultErrorModel } from "../NavigationResultErrorModel";
import { NavigationState } from "../states/NavigationState";
import { ValidationErrorModel } from "../ValidationErrorModel";
import { ValidationResultModel } from "../ValidationResultModel";

const serverSideValidatedQuestions = (): QuestionType[] => [
    QuestionType.CheckBox,
    QuestionType.ImageSelector,
    QuestionType.RadioButton,
    QuestionType.DropDownList,
    QuestionType.AutoComplete,
    QuestionType.Slider,
    QuestionType.NetPromoter,
    QuestionType.RespondentLanguage,
    QuestionType.RespondentTimeZone,
    QuestionType.NumericAnswer,
    QuestionType.CardSort,
    QuestionType.ImageAreaSelector,
    QuestionType.ImageAreaEvaluator,
    QuestionType.DateTimeAnswer,
    QuestionType.NumericRanking,
    QuestionType.ChoiceGrid,
    QuestionType.ImageChoiceGrid,
    QuestionType.StarRatingGrid,
    QuestionType.DragDropRanking,
    QuestionType.DrillDown,
    QuestionType.RunningTotal,
    QuestionType.HybridGrid,
    QuestionType.FileUpload,
    QuestionType.LookupTable,
    QuestionType.EmailAddressAnswer,
    QuestionType.RespondentEmail,
    QuestionType.RespondentPhone,
    QuestionType.TextAnswer,
    QuestionType.Signature,
    QuestionType.OpenEndAnswer,
    QuestionType.Carrousel,
];

export function isQuestionValidatedServerSide(
    questionType: QuestionType,
    useClientValidation: boolean,
): boolean {
    return !useClientValidation && serverSideValidatedQuestions().includes(questionType);
}

export function validationErrorConverter(
    navigationErrorResult: NavigationResultErrorModel[],
): ValidationResultModel {
    return {
        isValid: false,
        errorsByVariableName: navigationErrorResult.reduce(
            (obj, { variableName, message, softPrompt }) => {
                const key = variableName.split("$")[0];
                const varInfo = variableName.split("$")[1];
                if (!obj[key]) obj[key] = []; // eslint-disable-line no-param-reassign
                obj[key].push({ message, softPrompt, varInfo });
                return obj;
            },
            {},
        ),
    };
}

export function isValid(validationResult: ValidationResultModel): boolean {
    return (
        isEmpty(validationResult.errorsByVariableName) ||
        every(validationResult.errorsByVariableName, (errors) =>
            shouldIgnoreValidationErrors(errors),
        )
    );
}

// If all remaining errors are soft prompt that are already being prompted to the user
// Then the page is valid
export function shouldIgnoreValidationErrors(validationErrors: ValidationErrorModel[]) {
    return validationErrors.every((error) => error.softPrompt && !error.isErrorNew);
}

export const isVariableValidatedServerSide = (
    variableName: string,
    navigation: NavigationState,
    useClientValidation: boolean,
) => {
    const currentPageServerSideValidatedQuestions = navigation.currentPage?.questions?.filter((q) =>
        isQuestionValidatedServerSide(q.type, useClientValidation),
    );
    return currentPageServerSideValidatedQuestions?.some((q) =>
        [q.name, ...q.variableNames].some((v) => v.toUpperCase() === variableName.toUpperCase()),
    );
};
